import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Star, Clock, Users, Award, CheckCircle2, PlayCircle, BookOpen, Download, Share2, ChevronDown, Sparkles, ArrowRight } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { addToCart, getCart } from '../lib/cart';
import FloatingBadge from '../components/three/FloatingBadge';
import { useToast } from '../hooks/use-toast';

export default function CourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [openModule, setOpenModule] = useState(0);
  const [tab, setTab] = useState('overview');
  const [inCart, setInCart] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.course(id).then(c => { setCourse(c); setInCart(!!getCart().find(i => i.id === c.id)); }).catch(() => setCourse(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="pt-24 text-center py-20">Loading…</div>;
  if (!course) return (
    <div className="pt-24 max-w-xl mx-auto text-center py-20">
      <h1 className="text-3xl font-bold mb-2">Course not found</h1>
      <Link to="/courses"><Button className="mt-4">Browse courses</Button></Link>
    </div>
  );

  const handleAddCart = () => {
    addToCart(course);
    setInCart(true);
    toast({ title: 'Added to cart', description: course.title });
  };

  const handleEnroll = async () => {
    if (!user) { navigate('/login?redirect=/course/' + course.id); return; }
    try {
      await api.enroll(course.id);
      toast({ title: 'Enrollment successful!', description: `You are enrolled in ${course.title}` });
      navigate('/dashboard');
    } catch (e) {
      toast({ title: 'Enrollment failed', variant: 'destructive' });
    }
  };

  const totalHours = (course.modules || []).reduce((sum, m) => sum + m.hours, 0);

  return (
    <div className="pt-16">
      <section className="relative overflow-hidden bg-gradient-to-br from-[#1a0e3d] via-[#2a1265] to-[#4c1d95] text-white">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-[#ff5722]/30 rounded-full blur-3xl blob" />

        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-14 grid lg:grid-cols-[1fr_400px] gap-10 relative">
          <div>
            <Link to="/courses" className="text-sm text-purple-200 hover:text-white mb-3 inline-block">← All courses</Link>
            <div className="flex flex-wrap gap-2 mb-4">
              {course.badge && <span className="text-xs font-bold px-2.5 py-1 rounded-md bg-[#ff5722]">{course.badge}</span>}
              <span className="text-xs font-bold px-2.5 py-1 rounded-md bg-white/10 border border-white/20">{course.level}</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-extrabold leading-tight mb-3">{course.title}</h1>
            <p className="text-lg text-purple-100 mb-5">{course.tagline}</p>
            <div className="flex flex-wrap items-center gap-5 text-sm">
              <div className="flex items-center gap-1.5"><Star size={16} className="fill-amber-400 text-amber-400" /><span className="font-bold">{course.rating}</span><span className="opacity-70">({(course.reviews || 0).toLocaleString()} reviews)</span></div>
              <div className="flex items-center gap-1.5"><Users size={16} /> {(course.students || 0).toLocaleString()} learners</div>
              <div className="flex items-center gap-1.5"><Clock size={16} /> {course.duration}</div>
              <div className="flex items-center gap-1.5"><BookOpen size={16} /> {(course.modules || []).length} modules</div>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#ff5722] to-[#facc15] grid place-items-center font-bold">{(course.instructor || '?')[0]}</div>
              <div>
                <div className="text-sm opacity-80">Instructor</div>
                <div className="font-semibold">{course.instructor}</div>
              </div>
            </div>
          </div>

          <div className="lg:sticky lg:top-24 lg:self-start">
            <div className="bg-white text-gray-900 rounded-2xl overflow-hidden shadow-2xl border border-white/10">
              <div className="relative aspect-video overflow-hidden">
                <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover" />
                <div className={`absolute inset-0 bg-gradient-to-tr ${course.color} mix-blend-multiply opacity-40`} />
                <button className="absolute inset-0 grid place-items-center group">
                  <div className="w-16 h-16 rounded-full bg-white/95 grid place-items-center shadow-2xl group-hover:scale-110 transition-transform pulse-glow">
                    <PlayCircle size={40} className="text-[#6b47dc]" />
                  </div>
                </button>
              </div>
              <div className="p-6">
                <div className="flex items-baseline gap-3 mb-4">
                  <span className="text-3xl font-extrabold">${Number(course.discountPrice).toLocaleString()}</span>
                  <span className="text-gray-400 line-through">${Number(course.price).toLocaleString()}</span>
                  <span className="text-sm font-bold text-emerald-600">{Math.round(((course.price - course.discountPrice) / course.price) * 100)}% off</span>
                </div>
                <div className="text-xs text-[#ff5722] font-bold mb-4 flex items-center gap-1">
                  <Sparkles size={12} /> Limited time offer • Ends soon
                </div>
                <Button onClick={handleEnroll} className="w-full bg-[#ff5722] hover:bg-[#e64a19] text-white font-bold h-12 mb-2 btn-shine">
                  Enroll Now <ArrowRight size={16} className="ml-1" />
                </Button>
                <Button variant="outline" onClick={handleAddCart} disabled={inCart} className="w-full border-2 border-[#6b47dc] text-[#6b47dc] hover:bg-[#6b47dc] hover:text-white font-bold h-12">
                  {inCart ? 'Added to cart' : 'Add to cart'}
                </Button>
                <div className="mt-5 pt-5 border-t border-gray-100 space-y-2.5">
                  {(course.highlights || []).map(h => (
                    <div key={h} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" /><span>{h}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-5 pt-5 border-t border-gray-100 flex items-center justify-around">
                  <button className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#6b47dc]"><Share2 size={14} /> Share</button>
                  <button className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#6b47dc]"><Download size={14} /> Brochure</button>
                  <button className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#6b47dc]"><Award size={14} /> Certificate</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-8 border-b border-gray-100">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-sm font-bold uppercase tracking-wider text-gray-500">Skills you'll master:</span>
            {(course.skills || []).map(s => (
              <span key={s} className="px-3 py-1.5 rounded-lg bg-gradient-to-br from-purple-100 to-orange-100 text-[#6b47dc] font-semibold text-sm border border-purple-200">{s}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-10">
        <div className="grid lg:grid-cols-[1fr_320px] gap-10">
          <div>
            <div className="flex gap-1 border-b border-gray-200 mb-6">
              {[{ key: 'overview', label: 'Overview' }, { key: 'curriculum', label: 'Curriculum' }, { key: 'faq', label: 'FAQ' }].map(t => (
                <button key={t.key} onClick={() => setTab(t.key)} className={`px-5 py-3 font-bold text-sm border-b-2 transition-colors ${tab === t.key ? 'border-[#6b47dc] text-[#6b47dc]' : 'border-transparent text-gray-500 hover:text-gray-900'}`}>{t.label}</button>
              ))}
            </div>

            {tab === 'overview' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-extrabold">About this course</h2>
                <p className="text-gray-700 leading-relaxed">{course.description}</p>
                <h3 className="text-xl font-extrabold mt-6">What you'll learn</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {(course.highlights || []).concat((course.skills || []).map(s => `Hands-on with ${s}`)).map((h, i) => (
                    <div key={i} className="flex items-start gap-2"><CheckCircle2 size={18} className="text-emerald-500 mt-0.5 flex-shrink-0" /><span className="text-sm text-gray-700">{h}</span></div>
                  ))}
                </div>
              </div>
            )}

            {tab === 'curriculum' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-extrabold">Curriculum</h2>
                  <div className="text-sm text-gray-500">{(course.modules || []).length} modules • {totalHours} hours</div>
                </div>
                <div className="space-y-2">
                  {(course.modules || []).map((m, i) => (
                    <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                      <button onClick={() => setOpenModule(openModule === i ? -1 : i)} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-purple-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-white border border-purple-200 text-[#6b47dc] font-bold grid place-items-center text-sm">{i + 1}</div>
                          <div className="text-left">
                            <div className="font-bold text-gray-900">{m.title}</div>
                            <div className="text-xs text-gray-500">{m.hours} hours • {Math.ceil(m.hours / 2)} lessons</div>
                          </div>
                        </div>
                        <ChevronDown size={20} className={`text-gray-400 transition-transform ${openModule === i ? 'rotate-180' : ''}`} />
                      </button>
                      {openModule === i && (
                        <div className="p-4 bg-white space-y-2 border-t border-gray-100">
                          {[1, 2, 3, 4].map(n => (
                            <div key={n} className="flex items-center gap-3 py-2">
                              <PlayCircle size={18} className="text-[#6b47dc]" />
                              <span className="text-sm text-gray-700 flex-1">{m.title} — Lesson {n}</span>
                              <span className="text-xs text-gray-400">{15 + n * 5} min</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === 'faq' && (
              <div className="space-y-3">
                <h2 className="text-2xl font-extrabold mb-4">Frequently asked questions</h2>
                {[
                  { q: 'Who should take this course?', a: 'Anyone with basic programming knowledge looking to accelerate their career in ' + course.category.replace('-', ' ') + '.' },
                  { q: 'Are the classes live?', a: 'Yes, all classes are live and interactive. You will also get access to recordings for lifetime.' },
                  { q: 'What are the system requirements?', a: 'A laptop/desktop with at least 4GB RAM, a stable internet connection and a modern browser.' },
                  { q: 'Do you provide placement assistance?', a: 'Yes! We provide dedicated career coaching, mock interviews, and access to 3,000+ hiring partners.' },
                  { q: 'Can I get a refund?', a: 'Yes, we offer a 7-day money-back guarantee if you are not satisfied with the course.' },
                ].map((f, i) => (
                  <details key={i} className="group border border-gray-200 rounded-xl p-4 bg-white">
                    <summary className="cursor-pointer font-bold text-gray-900 flex items-center justify-between">{f.q}<ChevronDown size={16} className="group-open:rotate-180 transition-transform text-gray-400" /></summary>
                    <p className="mt-3 text-sm text-gray-600 leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            )}
          </div>

          <aside className="space-y-4">
            <div className="bg-gradient-to-br from-purple-50 to-orange-50 rounded-2xl p-5 border border-purple-100">
              <FloatingBadge color="#6b47dc" height="180px" />
              <h4 className="font-bold text-center text-gray-900 mt-2">Industry Certificate</h4>
              <p className="text-xs text-gray-500 text-center mt-1">Earn an industry-recognized certificate on completion.</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <h4 className="font-bold mb-3">This course includes</h4>
              <ul className="space-y-2.5 text-sm text-gray-700">
                <li className="flex items-center gap-2"><Clock size={14} className="text-[#6b47dc]" /> {totalHours} hours of live content</li>
                <li className="flex items-center gap-2"><BookOpen size={14} className="text-[#6b47dc]" /> {(course.modules || []).length} modules</li>
                <li className="flex items-center gap-2"><Download size={14} className="text-[#6b47dc]" /> Downloadable resources</li>
                <li className="flex items-center gap-2"><Award size={14} className="text-[#6b47dc]" /> Certificate of completion</li>
                <li className="flex items-center gap-2"><Users size={14} className="text-[#6b47dc]" /> 24/7 community support</li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
